import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ProductFeatureGated from 'dashboard/components/ProductFeatureGated';
import ReactSelect from 'dashboard/components/ReactSelect';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import { ProductFeatureName } from 'dashboard/models/organization';
import { Role } from 'dashboard/models/role';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

const StyledLabel = styled(Label)`
  display: flex;
  align-items: center;
`;

const StyledHelpIcon = styled(HelpIcon)`
  & div {
    display: flex;
  }
`;

interface FormNewCaseTemplateProps {
  roles: Role[];
  defaultValues: Partial<NewCaseTemplateFormData>;
  submit: (formData: NewCaseTemplateFormData) => void;
  handleCancel: () => void;
}

const FormNewCaseTemplate = ({
  roles,
  defaultValues,
  handleCancel,
  submit,
}: FormNewCaseTemplateProps) => {
  const roleOptions = roles.map((role) => ({
    label: role.attributes.name,
    value: role.id,
  }));

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors, isValid },
  } = useFormSecurely<NewCaseTemplateFormData>({
    defaultValues,
    mode: 'onChange',
  });

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Label size='md' htmlFor='form-clone-case-template__name' weight='bold'>
        Case template name
      </Label>
      <Spacer height={1} />
      <Input
        {...register('name', {
          required: 'Please give your case template a name',
          minLength: {
            value: 2,
            message: 'Case template names must be at least 2 letters long',
          },
        })}
        id='form-new-case-template__name'
        placeholder='Name'
        type='text'
      />
      {errors.name && <FormError>{errors.name.message}</FormError>}
      <Spacer height={3} />

      <StyledLabel size='sm' htmlFor='form-clone-case-template__case-prefix'>
        Case template key (optional)
        <StyledHelpIcon>
          Case template key must be between 2-4 letters long and only uppercase letters.
        </StyledHelpIcon>
      </StyledLabel>
      <Spacer height={1} />
      <Input
        {...register('case-prefix', {
          minLength: {
            value: 2,
            message: 'Case key must be at least 2 letters long',
          },
          pattern: {
            value: /^[a-zA-Z0-9_-]+$/,
            message: 'Case key can only contain alphanumeric characters, underscores, and hyphens.',
          },
        })}
        id='form-clone-case-template__case-prefix'
        data-test='form-clone-case-template__case-prefix'
        placeholder='Case template key'
        type='text'
      />
      {errors['case-prefix'] && <FormError>{errors['case-prefix'].message}</FormError>}

      <ProductFeatureGated productFeatureFlag={ProductFeatureName.RolesDashboard}>
        <Spacer height={3} />
        <StyledLabel size='sm' htmlFor='form-clone-case-template__role-ids'>
          Visible to roles
          <StyledHelpIcon>
            Select which role can view all cases and associated analytics from this case template.
            By default, only assigned cases are visible. This setting can also be updated in Roles
            menu in the future.
          </StyledHelpIcon>
        </StyledLabel>
        <Spacer height={1} />

        <Controller
          control={control}
          name='role-ids'
          rules={{ required: 'Please select at least one role that can view all cases' }}
          render={({ field: { onChange, value } }) => (
            <ReactSelect
              id='form-clone-case-template__role-ids'
              data-test='form-clone-case-template__role-ids'
              value={value}
              onChange={onChange}
              options={roleOptions}
              isMulti={true}
              portal={true}
            />
          )}
        />
        {errors['role-ids'] && <FormError>{(errors['role-ids'] as any)?.message}</FormError>}
      </ProductFeatureGated>
      <Spacer height={5} />

      <HStack equal={true}>
        <Button
          variant='basic'
          id='form-new-case-template__cancel'
          data-test='form-new-case-template__cancel'
          disabled={isSubmitting}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          type='submit'
          data-test='form-new-case-template__submit'
          disabled={isSubmitting || !isValid}
          isPending={isSubmitting}
        >
          Create
        </Button>
      </HStack>
    </form>
  );
};

export interface NewCaseTemplateFormData {
  name: string;
  'case-prefix': string;
  'role-ids': string[];
}

export default FormNewCaseTemplate;
